import { myFormatService } from 'cockpit/services/format/FormatService.connected';
import AuthService from 'lib/auth/AuthService';
import { myResourceApi } from 'v5/api/resource/resource.api.connected';
import { createShortlistApi } from 'v5/api/shortlist/shortlist.api.http';
import store from '../../store';
import { CONTACTS_ACTIONS } from '../../store/v1/contacts/contacts.constants';
import { openBookingSidebar } from '../../store/v1/ui/ui.actions';
import { apiClient } from '../../v4/core/apiClient';
import { myV1ActionEvent } from '../../v4/migration/actionAsEventMiddleware.connected';
import { queryClient } from '../../v4/migration/queryClientSingleton';
import { myAccessService } from './front/AccessService/AccessService.connected';
import { myErrorService } from './front/ErrorService/ErrorService.connected';
import { myFeatureManagementService } from './front/FeatureManagementService/FeatureManagementService.connected';
import { myFeatureService } from './front/FeatureService/FeatureService.connected';
import { myPermissionService } from './front/PermissionService/PermissionService.connected';
import { myRoutingService } from './front/RoutingService/RoutingService.connected';
import { mySupportService } from './front/SupportService/SupportService.connected';
import { myOnlineStatusService } from './front/OnlineStatusService/OnlineStatusService.connected';
import { myThemeService } from './front/ThemeService/ThemeService.connected';
import { myToastService } from './front/ToastService/ToastService.connected';
import { createUserFlowService } from './front/UserFlowService/UserFlowService.impl';
import { userPreferencesService } from './front/UserPreferencesService/UserPreferencesService.connected';
import { myGlobalSearchService } from './GlobalSearchService/GlobalSearchService.connected';
import { myAppScopeService } from './internal/AppScopeService/AppScopeService.connected';
import { myEnterpriseService } from './io/EnterpriseService/EnterpriseService.connected';
import { myContextService } from './internal/ContextService/ContextService.connected';
import { myGlobalSettingsService } from './internal/GlobalSettingsService/GlobalSettingsService.connected';
import { myLocationService } from './internal/LocationService/LocationService.connected';
import { myMessageService } from './internal/MessageService/MessageService.connected';
import { myMetastructureMappingService } from './internal/MetastructureMappingService/MetastructureMappingService.connected';
import { createMutationService } from './internal/MutationService/MutationService.impl';
import { myNavigationService } from './internal/NavigationService/NavigationService.connected';
import { myProductionTypeService } from './internal/ProductionTypeService/ProductionTypeService.connected';
import { myResourceTypeService } from './internal/ResourceTypeService/ResourceTypeService.connected';
import { myArrivalListService } from './io/ArrivalListService/ArrivalListService.connected';
import { myBookingMutationService } from './io/BookingMutationService/BookingMutationService.connected';
import { myBookingService } from './io/BookingService/BookingService.connected';
import { createBookingTypeService } from './io/BookingTypeService/BookingTypeService.impl';
import { myCalloutsService } from './io/CalloutsService/CalloutsService.connected';
import { myCalloutMutationService } from './io/CalloutMutationService/CalloutMutationService.connected';
import { myCallsheetMutationService } from './io/CallsheetMutationService/CallsheetMutationService.connected';
import { myCallsheetService } from './io/CallsheetService/CallsheetService.connected';
import { myEventService } from './io/EventService/EventService.connected';
import { myFrontDeskService } from './io/FrontDeskService/FrontDeskService.connected';
import { myGeolocationService } from './io/GeolocationService/GeolocationService.connected';
import { myGroupsService } from './io/GroupsService/GroupsService.connected';
import { myProductionMutationService } from './io/ProductionMutationService/ProductionMutationService.connected';
import { myProductionService } from './io/ProductionService/ProductionService.connected';
import { myReconciliationService } from './io/ReconciliationService/ReconciliationService.connected';
import { myResourceMutationService } from './io/ResourceMutationService/ResourceMutationService.connected';
import { createResourceService } from './io/ResourceService/ResourceService.impl';
import { myShortlistMutationService } from './io/ShortlistMutationService/ShortlistMutationService.connected';
import { createShortlistService } from './io/ShortlistService/ShortlistService.impl';
import { myStudioService } from './io/StudioService/StudioService.connected';
import { myTagsService } from './io/TagsService/TagsService.connected';
import { createUploadService } from './io/UploadService/UploadService.impl';
import { createVisitorManagementService } from './io/VisitorManagementService/VisitorManagementService.impl';
import { myVisitorVendorStateService } from './io/VisitorVendorStateService/VisitorVendorStateService.connected';
import { myLocaleService } from './LocaleService/LocaleService.connected';
import { myEnterpriseMutationService } from './io/EnterpriseMutationService/EnterpriseMutationService.connected';
import { myResourceTypeAdminService } from 'v5/features/resource-types-admin/ResourceTypeAdminService.connected';
import { myResourceTypeAdminMutationService } from 'v5/features/resource-types-admin/ResourceTypeAdminMutationService.connected';

const mutationService = createMutationService();

const uploadServiceInstance = createUploadService({
  services: { errorService: myErrorService }
});

const resourceServiceInstance = createResourceService({
  api: myResourceApi,
  queryClient,
  onCacheDump: function onCacheDump(resources) {
    store.dispatch({
      type: CONTACTS_ACTIONS.GET_BATCH_CONTACTS_SUCCESS,
      result: {
        data: resources
      }
    });
  },
  v1Event: myV1ActionEvent,
  services: {
    errorService: myErrorService,
    messageService: myMessageService
  }
});

/**
 * Stable reference to all services in the app.
 * This is used to inject services into components.
 * In the greenfield code, we have a root widget that receives stable services reference and pass it transparently down to all widgets.
 */
export const myServices = {
  arrivalListService: myArrivalListService,
  studioService: myStudioService,
  tagsService: myTagsService,
  groupsService: myGroupsService,
  frontDeskService: myFrontDeskService,
  eventService: myEventService,
  supportService: mySupportService,
  visitorManagementService: createVisitorManagementService({
    services: {
      eventService: myEventService,
      toastService: myToastService
    },
    getCurrentAccountId: () => {
      return AuthService.token()?.account?.id ?? null;
    },
    openBooking: (bookingId, options) => {
      store.dispatch(openBookingSidebar({ id: bookingId }, options));
    }
  }),
  visitorVendorStateService: myVisitorVendorStateService,
  localeService: myLocaleService,
  themeService: myThemeService,
  onlineStatusService: myOnlineStatusService,
  userPreferencesService,
  messageService: myMessageService,
  appScopeService: myAppScopeService,
  contextService: myContextService,
  globalSettingsService: myGlobalSettingsService,
  resourceTypeService: myResourceTypeService,
  productionTypeService: myProductionTypeService,
  metastructureMappingService: myMetastructureMappingService,
  mutationService,
  toastService: myToastService,
  featureService: myFeatureService,
  accessService: myAccessService,
  permissionService: myPermissionService,
  globalSearchService: myGlobalSearchService,
  featureManagementService: myFeatureManagementService,
  resourceService: resourceServiceInstance,
  userFlowService: createUserFlowService({
    services: {
      toastService: myToastService,
      mutationService,
      messageService: myMessageService,
      featureService: myFeatureService,
      callsheetMutationService: myCallsheetMutationService,
      calloutMutationService: myCalloutMutationService,
      calloutsService: myCalloutsService,
      userPreferencesService,
      callsheetService: myCallsheetService,
      productionService: myProductionService,
      productionTypeService: myProductionTypeService,
      metastructureMappingService: myMetastructureMappingService,
      resourceTypeService: myResourceTypeService,
      resourceService: resourceServiceInstance,
      tagsService: myTagsService,
      groupsService: myGroupsService,
      enterpriseMutationService: myEnterpriseMutationService,
      enterpriseService: myEnterpriseService,
      appScopeService: myAppScopeService,
      resourceMutationService: myResourceMutationService,
      productionMutationService: myProductionMutationService,
      navigationService: myNavigationService,
      uploadService: uploadServiceInstance
    }
  }),
  bookingTypeService: createBookingTypeService({
    services: { errorService: myErrorService }
  }),
  geolocationService: myGeolocationService,
  bookingService: myBookingService,
  uploadService: uploadServiceInstance,
  routingService: myRoutingService,
  locationService: myLocationService,
  navigationService: myNavigationService,
  productionService: myProductionService,
  productionMutationService: myProductionMutationService,
  callsheetService: myCallsheetService,
  callsheetMutationService: myCallsheetMutationService,
  resourceMutationService: myResourceMutationService,
  bookingMutationService: myBookingMutationService,
  shortlistMutationService: myShortlistMutationService,
  shortlistService: createShortlistService({
    services: {
      errorService: myErrorService,
      messageService: myMessageService
    },
    api: createShortlistApi({
      client: apiClient,
      services: {
        metastructureMappingService: myMetastructureMappingService
      }
    }),
    queryClient,
    v1Event: myV1ActionEvent
  }),
  calloutsService: myCalloutsService,
  reconciliationService: myReconciliationService,
  formatService: myFormatService,
  enterpriseService: myEnterpriseService,
  enterpriseMutationService: myEnterpriseMutationService,
  resourceTypeAdminService: myResourceTypeAdminService,
  resourceTypeAdminMutationService: myResourceTypeAdminMutationService
};

/** Injects root `myServices` and omits `services` from the returned component props. */
export function withRootServices<TProps extends object>(
  Component: React.ComponentType<TProps>
): React.ComponentType<Omit<TProps, 'services'>> {
  return function WithRootServices(props: Omit<TProps, 'services'>) {
    const propsWithServices = { ...props, services: myServices } as TProps;
    return <Component {...propsWithServices} />;
  };
}
